import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { NotificationPreference } from '@hooks/notifications';

export interface OptionProps {
  value: NotificationPreference;
  name: string;
  register: any;
  emoji: string;
  title: string;
  description: string;
  extraInfo?: React.ReactNode;
  highlightColor?: 'green' | 'blue';
  testId: string;
  watch: any;
  setValue?: (name: string, value: any) => void;
}

const Option: React.FC<OptionProps> = ({
  value,
  name,
  register,
  emoji,
  title,
  description,
  extraInfo,
  highlightColor = 'green',
  testId,
  watch
}) => {
  const radioId = `marketing-${value}`;

  const colors = useMemo(() => {
    const colorClasses = {
      green: {
        border: 'border-green-500',
        bg: 'bg-green-50',
        ring: 'ring-green-500',
        text: 'text-green-800',
        textDescription: 'text-green-700',
        selected: {
          border: 'border-green-600 bg-green-600'
        }
      },
      blue: {
        border: 'border-gray-300',
        bg: 'bg-gray-50',
        ring: 'ring-blue-400',
        text: 'text-gray-900',
        textDescription: 'text-gray-500',
        selected: {
          border: 'border-blue-500 bg-blue-500'
        }
      }
    };

    return colorClasses[highlightColor];
  }, [highlightColor]);

  const handleCardClick = () => {
    const syntheticEvent = {
      target: {
        name,
        value,
        type: 'radio',
        checked: true
      }
    };

    if (register(name).onChange) {
      register(name).onChange(syntheticEvent);
    }
  };

  return (
    <div className="relative m-2">
      <input
        type="radio"
        id={radioId}
        value={value}
        data-testid={testId}
        className="peer sr-only"
        {...register(name)}
      />
      <motion.label
        htmlFor={radioId}
        className={`flex items-start border ${colors.border} ${
          colors.bg
        } shadow-sm rounded-lg cursor-pointer hover:shadow-md transition-all duration-200 
        ${
          watch(name) === value
            ? `ring-2 ring-offset-2 ${
                highlightColor === 'green' ? 'ring-green-500' : 'ring-blue-400'
              }`
            : ''
        }`}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleCardClick}
      >
        <div className="p-4 sm:p-6 flex">
          <div className="flex-shrink-0 pt-1">
            <div
              className={`w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center 
            ${
              watch(name) === value
                ? highlightColor === 'green'
                  ? 'border-green-600 bg-green-600'
                  : 'border-blue-500 bg-blue-500'
                : ''
            }`}
            >
              <div
                className={`w-2.5 h-2.5 bg-white rounded-full ${
                  watch(name) === value ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>
          <div className="ml-3 text-xs sm:text-sm flex-1">
            <div className={`flex items-center font-bold ${colors.text}`}>
              <span className="mr-2 text-lg inline-flex items-center justify-center">
                {emoji}
              </span>
              <span className="flex-1">{title}</span>
            </div>
            <div className={`mt-2 ${colors.textDescription} leading-relaxed`}>
              {description}
            </div>
            {extraInfo}
          </div>
        </div>
      </motion.label>
    </div>
  );
};

// Remove memo to ensure re-renders when form values change
export default Option;
